import { supabase } from '@/integrations/supabase/client';

export interface Expertise {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  imageUrl: string;
  buttonText: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

function mapExpertise(item: any): Expertise {
  return {
    id: item.id,
    title: item.title,
    subtitle: item.subtitle,
    description: item.description,
    imageUrl: item.image_url,
    buttonText: item.button_text,
    sortOrder: item.sort_order,
    isActive: item.is_active,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
  };
}

export async function getExpertises(): Promise<Expertise[]> {
  const { data, error } = await supabase
    .from('expertises')
    .select('*')
    .eq('is_active', true)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapExpertise);
}

export async function getAllExpertises(): Promise<Expertise[]> {
  const { data, error } = await supabase
    .from('expertises')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return (data || []).map(mapExpertise);
}

export async function getExpertiseById(id: string): Promise<Expertise | null> {
  const { data, error } = await supabase
    .from('expertises')
    .select('*')
    .eq('id', id)
    .single();

  if (error) return null;
  return mapExpertise(data);
}

export async function updateExpertise(id: string, updates: Partial<{
  title: string;
  subtitle: string;
  description: string;
  imageUrl: string;
  buttonText: string;
  sortOrder: number;
  isActive: boolean;
}>): Promise<Expertise> {
  const dbUpdates: any = { updated_at: new Date().toISOString() };
  if (updates.title !== undefined) dbUpdates.title = updates.title;
  if (updates.subtitle !== undefined) dbUpdates.subtitle = updates.subtitle;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.imageUrl !== undefined) dbUpdates.image_url = updates.imageUrl;
  if (updates.buttonText !== undefined) dbUpdates.button_text = updates.buttonText;
  if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder;
  if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;

  const { data, error } = await supabase
    .from('expertises')
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return mapExpertise(data);
}

export async function reorderExpertises(orderedIds: string[]): Promise<void> {
  for (let i = 0; i < orderedIds.length; i++) {
    await supabase
      .from('expertises')
      .update({ sort_order: i + 1, updated_at: new Date().toISOString() })
      .eq('id', orderedIds[i]);
  }
}

export async function toggleExpertiseActive(id: string): Promise<Expertise> {
  const current = await getExpertiseById(id);
  if (!current) throw new Error('Expertise not found');

  return updateExpertise(id, { isActive: !current.isActive });
}
